import { useState, useEffect } from 'react';
import { Search, ExternalLink, Sparkles, Plus, X, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DOMAIN = 'douchelezer.recrasolutions.com';
const DEFAULT_KEYWORDS = [
    'douchelezer kopen',
    'contactloos betalen douche',
    'Adyen S1U2 camping',
    'betaalterminal douche',
    'muntjes vervangen douche',
];

const gscQueryLink = (keyword) => {
    const base = 'https://search.google.com/search-console/performance/search-analytics';
    const params = new URLSearchParams({
        resource_id: `sc-domain:recrasolutions.com`,
        query: keyword,
    });
    return `${base}?${params.toString()}`;
};

const gscInspectLink = () =>
    `https://search.google.com/search-console/inspect?resource_id=sc-domain:recrasolutions.com&id=https://${DOMAIN}/`;

const googleSearchLink = (keyword) => `https://www.google.com/search?q=${encodeURIComponent(keyword + ' site:' + DOMAIN)}`;

export default function SearchRankingWidget({ token }) {
    const [keywords, setKeywords] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [newKeyword, setNewKeyword] = useState('');

    // Persist keywords in localStorage (no backend storage for MVP)
    useEffect(() => {
        const saved = localStorage.getItem('seo_keywords');
        if (saved) {
            setKeywords(JSON.parse(saved));
        } else {
            setKeywords(DEFAULT_KEYWORDS);
            localStorage.setItem('seo_keywords', JSON.stringify(DEFAULT_KEYWORDS));
        }
    }, []);

    const saveKeywords = (list) => {
        setKeywords(list);
        localStorage.setItem('seo_keywords', JSON.stringify(list));
    };

    const addKeyword = () => {
        if (!newKeyword.trim()) return;
        saveKeywords([...keywords, newKeyword.trim()]);
        setNewKeyword('');
        setShowAdd(false);
    };

    const removeKeyword = (kw) => saveKeywords(keywords.filter((k) => k !== kw));

    return (
        <div className="bg-gray-900 border border-gray-800 rounded-xl" data-testid="search-ranking-widget">
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        Zoekprestaties (Google Search Console)
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">{DOMAIN} | Volg positie per keyword</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowAdd(true)}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs"
                        data-testid="add-keyword-btn"
                    >
                        <Plus className="w-3 h-3" /> Keyword
                    </button>
                    <a
                        href={gscInspectLink()}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg text-xs"
                        data-testid="open-gsc-btn"
                    >
                        <ExternalLink className="w-3 h-3" /> Open GSC
                    </a>
                </div>
            </div>

            <div className="divide-y divide-gray-800">
                {keywords.map((kw) => (
                    <div key={kw} className="px-5 py-3 flex items-center justify-between group" data-testid={`keyword-row-${kw.replace(/\s+/g, '-')}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Sparkles className="w-3 h-3 text-purple-400 flex-shrink-0" />
                            <span className="text-sm text-gray-300 truncate">{kw}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <a
                                href={googleSearchLink(kw)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-gray-500 hover:text-white px-2 py-1"
                                title="Test in Google"
                            >
                                Google
                            </a>
                            <a
                                href={gscQueryLink(kw)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-emerald-400 hover:text-emerald-300 px-2 py-1"
                                title="Bekijk in Search Console"
                            >
                                GSC data
                            </a>
                            <button
                                onClick={() => removeKeyword(kw)}
                                className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 p-1 transition-opacity"
                                data-testid={`remove-keyword-${kw.replace(/\s+/g, '-')}`}
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                ))}
                {keywords.length === 0 && (
                    <div className="px-5 py-6 text-center text-sm text-gray-600">
                        Nog geen keywords. Voeg er een toe om positie te volgen.
                    </div>
                )}
            </div>

            <div className="px-5 py-3 border-t border-gray-800 bg-gray-950/50 text-xs text-gray-600">
                Klik op <span className="text-emerald-400">GSC data</span> voor de laatste positie/vertoningen/CTR van Google. Automatische ranking-import komt zodra GSC API-toegang is gekoppeld.
            </div>

            {showAdd && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="add-keyword-modal">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold">Nieuwe keyword</h3>
                            <button onClick={() => setShowAdd(false)} className="text-gray-500 hover:text-white">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <input
                            autoFocus
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addKeyword()}
                            placeholder="bijv. contactloze douchelezer camping"
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                            data-testid="new-keyword-input"
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-gray-400 hover:text-white text-sm">
                                Annuleren
                            </button>
                            <button
                                onClick={addKeyword}
                                disabled={!newKeyword.trim()}
                                className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-sm font-medium disabled:opacity-50"
                                data-testid="save-keyword-btn"
                            >
                                Toevoegen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
